/**
 * SecondaryPlaylist Component
 * Browse and drag tracks from a secondary playlist into workbench
 */

import { useState } from 'react';
import { ListMusic, Loader2, AlertCircle } from 'lucide-react';
import { usePlaylists, usePlaylist } from '../../api/hooks/usePlaylists';
import { SearchResultItem } from './SearchResultItem';

export function SecondaryPlaylist() {
  const [selectedPlaylistId, setSelectedPlaylistId] = useState<string>('');

  // Fetch user's playlists for dropdown
  const { data: playlistsData, isLoading: isLoadingPlaylists } = usePlaylists({
    limit: 50,
  });

  // Fetch selected playlist details
  const { data: playlistDetail, isLoading: isLoadingDetail } = usePlaylist(
    selectedPlaylistId || undefined
  );

  return (
    <div className="flex flex-col h-full">
      {/* Playlist Selector */}
      <div className="p-4 border-b border-zinc-800">
        <label className="block text-xs font-medium text-zinc-400 mb-2">
          Select Playlist
        </label>

        {isLoadingPlaylists ? (
          <div className="flex items-center justify-center py-2">
            <Loader2 className="w-4 h-4 text-emerald-500 animate-spin" />
          </div>
        ) : (
          <select
            value={selectedPlaylistId}
            onChange={(e) => setSelectedPlaylistId(e.target.value)}
            className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-md text-sm text-zinc-100 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          >
            <option value="">Choose a playlist...</option>
            {playlistsData?.playlists.map((playlist) => (
              <option key={playlist.id} value={playlist.id}>
                {playlist.name} ({playlist.tracksCount} tracks)
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Playlist Tracks */}
      <div className="flex-1 overflow-y-auto">
        {/* No playlist selected */}
        {!selectedPlaylistId && (
          <div className="flex flex-col items-center justify-center h-full px-6 py-12 text-center">
            <ListMusic className="w-12 h-12 text-zinc-700 mb-3" />
            <p className="text-sm text-zinc-500">
              Select a playlist to browse tracks
            </p>
          </div>
        )}

        {/* Loading playlist details */}
        {selectedPlaylistId && isLoadingDetail && (
          <div className="flex flex-col items-center justify-center h-full py-12">
            <Loader2 className="w-8 h-8 text-emerald-500 animate-spin mb-3" />
            <p className="text-sm text-zinc-500">Loading tracks...</p>
          </div>
        )}

        {/* Playlist tracks list */}
        {selectedPlaylistId && !isLoadingDetail && playlistDetail && (
          <div className="p-4 space-y-1">
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-zinc-100">
                {playlistDetail.name}
              </h3>
              <span className="text-xs text-zinc-500">
                {playlistDetail.tracks.items.length} tracks
              </span>
            </div>

            {/* Tracks */}
            {playlistDetail.tracks.items.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <AlertCircle className="w-10 h-10 text-zinc-700 mb-2" />
                <p className="text-sm text-zinc-500">This playlist is empty</p>
              </div>
            ) : (
              playlistDetail.tracks.items.map((playlistTrack) => (
                <SearchResultItem
                  key={playlistTrack.track.id}
                  track={playlistTrack.track}
                />
              ))
            )}
          </div>
        )}

        {/* Error state (playlist failed to load) */}
        {selectedPlaylistId && !isLoadingDetail && !playlistDetail && (
          <div className="flex flex-col items-center justify-center h-full px-6 py-12 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mb-3" />
            <p className="text-sm text-red-400">Failed to load playlist</p>
          </div>
        )}
      </div>
    </div>
  );
}
